<template>
  <tr class="group" :class="`group-index-${depth}`">
    <td>
      <div
        class="bm-grid-row-controls"
        :style="{ marginLeft: firstColumnIndentPx + 'px' }"
      >
        <bm-grid-row-expander
          :is-expanded="group.isExpanded"
          @toggle="emit('toggle-expand')"
        />
        <bm-grid-row-selector
          v-if="allowSelect"
          :checked="group.isSelected && rowSelectable(group)"
          :disabled="!rowSelectable(group)"
          :is-header="false"
          @change="emit('toggle-select')"
        />
        {{ groupTitle }}
      </div>
    </td>
    <td v-for="col in aggregateColumns" :key="col.columnKey">
      <bm-grid-aggregate-cell
        v-if="col.aggregateData"
        :column="visibleColumns[col.columnKey]"
        :summary="col.aggregateData.summary"
        :value="col.aggregateData.value"
        :align="col.align"
      />
    </td>
  </tr>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type {
  BmGridColumnConfigurations,
  ColumnAlignment,
  RowGroup,
} from "@/components/controls/grid/BmGridData";
import { SummaryPlacement } from "@/components/controls/grid/BmGridData";
import BmGridAggregateCell from "@/components/controls/grid/BmGridAggregateCell.vue";
import BmGridRowExpander from "@/components/controls/grid/BmGridRowExpander.vue";
import BmGridRowSelector from "@/components/controls/grid/BmGridRowSelector.vue";

interface BmGridTableGroupRowProps {
  group: RowGroup;
  depth: number;
  visibleColumns: BmGridColumnConfigurations;
  groupedColumns: BmGridColumnConfigurations;
  allowSelect: boolean;
  firstColumnIndentPx: number;
  rowSelectable: (item: unknown) => boolean;
}

const props = defineProps<BmGridTableGroupRowProps>();

interface BmGridTableGroupRowEvents {
  (event: "toggle-expand"): void;
  (event: "toggle-select"): void;
}

const emit = defineEmits<BmGridTableGroupRowEvents>();

type AggregateColumn = {
  columnKey: string;
  aggregateData: RowGroup["aggregateValues"][string] | undefined;
  align: ColumnAlignment;
};

const groupTitle = computed(
  () =>
    `${props.groupedColumns[props.group.columnKey].headingText}: ${props.group.columnValue}`
);

const aggregateColumns = computed((): AggregateColumn[] =>
  Object.keys(props.visibleColumns)
    .slice(1)
    .map((columnKey) => {
      const aggregateData = props.group.aggregateValues[columnKey];
      let align: ColumnAlignment = "start";
      if (aggregateData) {
        align =
          aggregateData.summary.placement === SummaryPlacement.Column
            ? props.visibleColumns[columnKey].align
            : aggregateData.summary.align;
      }
      return { columnKey, aggregateData, align };
    })
);
</script>

<style scoped lang="sass">
@import "src/sass/typography"
@import "src/sass/colours"

@mixin field-label
  @include bm-field-label

tr.group
  @include field-label
  padding-left: 0
  height: 36px

td
  @include bm-body2
  color: $bm-color-text01
  border-bottom: 1px solid #D1CFD7

.bm-grid-row-controls
  display: flex
  align-items: center
</style>
